import { useCallback, useEffect, useMemo, useState } from 'react';
import { listHotelRates, money, occupancyLabel, dateRange } from '../../data/hotels';
import HotelRateForm from './HotelRateForm';

const PAGE = 100;

// The same rows read three ways. Date range first: seasons are what move the price most.
const GROUPS = [['window', 'By date range'], ['room', 'By room'], ['occ', 'By guests']];

const chip = (on) => ({
  padding:'8px 13px', borderRadius:11, fontSize:12.5, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap',
  color: on ? '#fff' : '#5a726f', border:'1px solid ' + (on ? 'transparent' : '#f2e2d2'),
  background: on ? 'linear-gradient(135deg,#26c6a3,#0e8a9c)' : 'rgba(255,255,255,0.85)',
});

const card = (on) => ({
  padding:'11px 13px', borderRadius:13, cursor:'pointer', textAlign:'left',
  border:'1px solid ' + (on ? 'rgba(14,138,156,0.5)' : 'rgba(14,138,122,0.14)'),
  background: on ? 'rgba(38,198,163,0.12)' : 'rgba(255,255,255,0.7)',
});

/** A supplier offer of "Ordinary" means no offer at all — don't dress it up as one. */
const offerLabel = (o) => (!o || /^ordinary$/i.test(o) ? null : o);

/**
 * One hotel's whole rate table. The site shows a single "from" price; this is what that number
 * was chosen out of — every room, every occupancy, every date window, with cost beside sell.
 */
export default function HotelRates({ hotel, onClose }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [options, setOptions] = useState(null);   // facets from the unfiltered load, for the dropdowns
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [group, setGroup] = useState('window');
  const [room, setRoom] = useState('');
  const [occ, setOcc] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [sellable, setSellable] = useState(true);
  const [editing, setEditing] = useState(null);   // a rate to edit, or 'new'

  const params = useMemo(() => ({
    roomCategoryCode: room || undefined,
    accommodationCode: occ || undefined,
    stayFrom: from || undefined,
    stayTo: to || undefined,
    onlySellable: sellable ? undefined : false,
  }), [room, occ, from, to, sellable]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const d = await listHotelRates(hotel.id, { ...params, skip: 0, take: PAGE });
      setData(d);
      setRows(d.rows || []);
      // Keep the first, unfiltered facet lists: the dropdowns must not shrink as you filter.
      setOptions(o => o || { rooms: d.rooms || [], occupancies: d.occupancies || [] });
    } catch (e) { setError(e.message || 'Could not load the prices.'); }
    finally { setLoading(false); }
  }, [hotel.id, params]);

  useEffect(() => { load(); }, [load]);

  const loadMore = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await listHotelRates(hotel.id, { ...params, skip: rows.length, take: PAGE });
      setRows(rs => [...rs, ...(d.rows || [])]);
    } catch (e) { setError(e.message || 'Could not load more prices.'); }
    finally { setLoadingMore(false); }
  };

  const clearAll = () => { setRoom(''); setOcc(''); setFrom(''); setTo(''); };
  const filtered = !!(room || occ || from || to);

  const pickWindow = (w) => {
    const on = from === w.beginDate && to === w.endDate;
    if (on) { setFrom(''); setTo(''); } else { setFrom(w.beginDate); setTo(w.endDate); }
  };

  const groups = data
    ? group === 'window'
      ? (data.windows || []).map(w => ({
          key: `${w.beginDate}|${w.endDate}`, label: dateRange(w.beginDate, w.endDate),
          count: w.rateCount, from: w.sellFrom, to: w.sellTo,
          on: from === w.beginDate && to === w.endDate, pick: () => pickWindow(w),
        }))
      : group === 'room'
        ? (data.rooms || []).map(r => ({
            key: r.key, label: r.label, count: r.rateCount, from: r.sellFrom, to: r.sellTo,
            on: room === r.key, pick: () => setRoom(room === r.key ? '' : r.key),
          }))
        : (data.occupancies || []).map(o => ({
            key: o.key, label: occupancyLabel(o.key), count: o.rateCount, from: o.sellFrom, to: o.sellTo,
            on: occ === o.key, pick: () => setOcc(occ === o.key ? '' : o.key),
          }))
    : [];

  const showBoard = (data?.mealPlans?.length || 0) > 1;

  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, zIndex:130, display:'flex', alignItems:'center', justifyContent:'center', padding:'3vh 2vw', background:'rgba(6,40,45,0.42)', backdropFilter:'blur(5px)' }}>
      <div onClick={e => e.stopPropagation()} style={{ width:'100%', maxWidth:1180, maxHeight:'94vh', display:'flex', flexDirection:'column', borderRadius:22, overflow:'hidden', background:'rgba(255,255,255,0.95)', backdropFilter:'blur(28px) saturate(180%)', boxShadow:'0 30px 80px rgba(6,40,45,0.32)' }}>

        <div style={{ padding:'20px 24px 0', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'flex-start', gap:12 }}>
            <div style={{ flex:1 }}>
              <div className="serif" style={{ fontSize:23, lineHeight:1.15 }}>{data?.hotelName || hotel.displayName || hotel.name}</div>
              <div style={{ fontSize:12.5, color:'#5a726f', marginTop:4 }}>
                {data
                  ? `${data.total.toLocaleString()} of ${data.totalAll.toLocaleString()} prices${data.manualCount ? ` · ${data.manualCount} yours` : ''} · ${data.currencyCode} · ${data.appliedMarginPercent}% margin on supplier prices`
                  : 'Loading…'}
              </div>
            </div>
            <span onClick={() => setEditing('new')} className="a-primary" style={{ padding:'10px 16px', borderRadius:12, fontSize:13.5, cursor:'pointer', whiteSpace:'nowrap' }}>+ Add price</span>
            <span onClick={onClose} style={{ cursor:'pointer', fontSize:22, color:'#5a726f', lineHeight:1 }}>×</span>
          </div>

          {data?.priceOverride != null && (
            <div style={{ marginTop:12, padding:'10px 14px', borderRadius:12, fontSize:12.5, fontWeight:700, background:'rgba(26,122,72,0.12)', color:'#1a7a48' }}>
              This hotel sells at a fixed {money(data.priceOverride)}. The prices below are what the margin
              would have produced — they are not what customers pay.
            </div>
          )}

          <div style={{ display:'flex', gap:9, flexWrap:'wrap', alignItems:'center', margin:'14px 0 12px' }}>
            <select value={room} onChange={e => setRoom(e.target.value)} className="a-input" style={{ width:'auto', maxWidth:260, padding:'9px 12px' }}>
              <option value="">Any room</option>
              {(options?.rooms || []).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
            <select value={occ} onChange={e => setOcc(e.target.value)} className="a-input" style={{ width:'auto', maxWidth:260, padding:'9px 12px' }}>
              <option value="">Any guests</option>
              {(options?.occupancies || []).map(o => <option key={o.key} value={o.key}>{occupancyLabel(o.key)}</option>)}
            </select>
            <label style={{ display:'flex', alignItems:'center', gap:7, fontSize:12.5, color:'#5a726f', fontWeight:700 }}>
              Staying
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} className="a-input" style={{ width:'auto', padding:'8px 10px' }} />
              to
              <input type="date" value={to} onChange={e => setTo(e.target.value)} className="a-input" style={{ width:'auto', padding:'8px 10px' }} />
            </label>
            <span onClick={() => setSellable(v => !v)} style={chip(sellable)} title="Hides prices that have expired or whose booking window has closed">On sale now</span>
            {filtered && <span onClick={clearAll} style={{ cursor:'pointer', fontSize:12.5, fontWeight:700, color:'#5a726f' }}>Clear filters</span>}
          </div>

          <div className="glass" style={{ display:'flex', gap:4, padding:5, borderRadius:14, width:'fit-content' }}>
            {GROUPS.map(([k, label]) => (
              <span key={k} onClick={() => setGroup(k)} className={'a-subtab' + (group === k ? ' active' : '')} style={{ padding:'8px 14px', fontSize:12.5 }}>{label}</span>
            ))}
          </div>
        </div>

        <div style={{ padding:'14px 24px 22px', overflowY:'auto' }}>
          {error && (
            <div style={{ padding:'12px 16px', borderRadius:13, marginBottom:14, background:'rgba(192,73,47,0.1)', color:'#c0492f', fontWeight:700, fontSize:13.5 }}>
              {error} <span onClick={() => setError(null)} style={{ cursor:'pointer', marginLeft:8, opacity:.7 }}>dismiss</span>
            </div>
          )}

          {loading ? (
            <div style={{ padding:'56px 12px', color:'#5a726f' }}>Loading prices…</div>
          ) : (data?.total || 0) === 0 ? (
            <div style={{ padding:'52px 24px', textAlign:'center' }}>
              <div className="serif" style={{ fontSize:21, marginBottom:8 }}>No prices match those filters</div>
              <div style={{ fontSize:13.5, color:'#5a726f' }}>
                {data?.totalAll ? 'Try clearing a filter, or switch off "On sale now" to include expired prices.'
                                : 'The supplier sends no prices for this hotel — add your own and it can be sold.'}
              </div>
              <div onClick={() => setEditing('new')} className="a-primary" style={{ display:'inline-block', marginTop:16, padding:'11px 20px', borderRadius:12, fontSize:13.5, cursor:'pointer' }}>+ Add a price</div>
            </div>
          ) : (
            <>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(196px,1fr))', gap:9, marginBottom:18 }}>
                {groups.map(g => (
                  <div key={g.key} onClick={g.pick} style={card(g.on)}>
                    <div style={{ fontSize:12.5, fontWeight:800, marginBottom:3 }}>{g.label}</div>
                    <div style={{ fontSize:14, fontWeight:800, color:'#0c6d7c', fontVariantNumeric:'tabular-nums' }}>
                      {g.from === g.to ? money(g.from) : `${money(g.from)} – ${money(g.to)}`}
                    </div>
                    <div style={{ fontSize:11, color:'#5a726f', marginTop:2 }}>{g.count.toLocaleString()} price{g.count === 1 ? '' : 's'}</div>
                  </div>
                ))}
              </div>

              <div style={{ overflowX:'auto' }}>
                <table className="a-table" style={{ minWidth:880 }}>
                  <thead>
                    <tr>
                      <th>Staying</th><th>Room</th><th>Guests</th>
                      {showBoard && <th>Board</th>}
                      <th>You pay → you charge</th><th>Min stay</th><th>Offer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map(r => (
                      <tr key={r.id} style={r.isManual ? { background:'rgba(38,198,163,0.07)' } : undefined}>
                        <td style={{ whiteSpace:'nowrap' }}>{dateRange(r.beginDate, r.endDate)}</td>
                        <td>
                          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                            <span style={{ fontWeight:700 }}>{r.roomName || r.roomCategoryCode || '—'}</span>
                            {r.isManual && (
                              <span onClick={() => setEditing(r)} title={r.note || 'Your price — click to edit'}
                                style={{ padding:'2px 8px', borderRadius:100, fontSize:10, fontWeight:800, letterSpacing:'.05em', textTransform:'uppercase', cursor:'pointer', background:'rgba(26,122,72,0.14)', color:'#1a7a48' }}>Yours</span>
                            )}
                          </div>
                          {r.roomTypeCode && <div style={{ fontSize:11, color:'#5a726f', marginTop:2 }}>{r.roomTypeCode}</div>}
                        </td>
                        <td style={{ whiteSpace:'nowrap' }}>{occupancyLabel(r.accommodationCode)}</td>
                        {showBoard && <td>{r.mealTypeCode || '—'}</td>}
                        <td>
                          <span style={{ display:'flex', alignItems:'baseline', gap:7, fontVariantNumeric:'tabular-nums' }}>
                            {!r.isManual && <>
                              <span style={{ color:'#5a726f', fontSize:12.5 }}>{money(r.supplierPrice)}</span>
                              <span style={{ color:'rgba(12,43,47,0.3)', fontSize:11 }}>→</span>
                            </>}
                            <span style={{ fontWeight:800, fontSize:15 }}>{money(r.sellPrice)}</span>
                            {r.perRoom && <span style={{ fontSize:10.5, color:'#5a726f', whiteSpace:'nowrap' }}>per room</span>}
                          </span>
                        </td>
                        <td style={{ fontVariantNumeric:'tabular-nums', whiteSpace:'nowrap' }}>{r.minimumStay > 0 ? `${r.minimumStay} night${r.minimumStay === 1 ? '' : 's'}` : '—'}</td>
                        <td style={{ fontSize:12, color:'#5a726f' }}
                          title={r.bookingBegin || r.bookingEnd ? `Bookable ${dateRange(r.bookingBegin, r.bookingEnd)}` : ''}>
                          {offerLabel(r.offer) || '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {rows.length < (data?.total || 0) && (
                <div style={{ display:'flex', alignItems:'center', gap:12, justifyContent:'center', paddingTop:14 }}>
                  <button onClick={loadMore} disabled={loadingMore} className="a-ghost"
                    style={{ padding:'11px 22px', borderRadius:12, fontSize:13.5, cursor:'pointer', opacity: loadingMore ? 0.6 : 1 }}>
                    {loadingMore ? 'Loading…' : `Load ${Math.min(PAGE, data.total - rows.length)} more`}
                  </button>
                  <span style={{ fontSize:12.5, color:'#5a726f' }}>
                    showing {rows.length.toLocaleString()} of {data.total.toLocaleString()}
                  </span>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {editing && (
        <HotelRateForm
          hotel={hotel}
          rate={editing === 'new' ? null : editing}
          rooms={options?.rooms || []}
          boards={data?.mealPlans || []}
          onClose={() => setEditing(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
